import Modal from '@/components/common/modals/Modal';
import ModalManager, { useModal } from '@/components/ModalManager';
import { useTrackModalShown } from '@/services/analytics/useTracking';
import { Goal } from '@common/goals';
import {
    getFullWithdrawalDescription,
    getMyGoalAccount,
    useGoalAccount,
    useGoalFromGoalAccountId,
    useOnetimeTransactionSourceBalance,
    usePendingWithdrawalAmount,
} from '@common/react/goals';
import { dt } from '@common/util/dateTime';
import { useState } from 'react';
import { FrameRenderer } from '../common/SlideAnimator';
import TransactionAmountCard from './TransactionAmountCard';
import TransactionCompleteCard from './TransactionCompleteCard';
import TransactionConfirmationCard from './TransactionConfirmationCard';
import TransactionDestinationGoalCard from './TransactionDestinationGoalCard';
import TransactionSlideAnimator from './TransactionSlideAnimator';

interface OnetimeGoalToGoalFlowProps {
    sourceId: string;
    goalId: string;
}

const OnetimeGoalToGoalFlow = ({
    sourceId,
    goalId,
}: OnetimeGoalToGoalFlowProps) => {
    useTrackModalShown('One-time Goal to Goal Flow');

    const goalDirection = 'GOAL_TO_GOAL';
    const isCappedAtAvailableBalance = true;
    const modal = useModal();
    const sourceGoal = useGoalFromGoalAccountId(sourceId);
    const { sourceBalance } = useOnetimeTransactionSourceBalance(sourceId);
    const { pendingWithdrawalAmount } = usePendingWithdrawalAmount(sourceId);
    const sourceGoalAccount = useGoalAccount(sourceId);
    const [destinationGoal, setDestinationGoal] = useState<Goal>();
    const [amount, setAmount] = useState(0);

    if (!sourceGoal) {
        // This should never happen
        console.warn('Failed to find source goal ID in OnetimeGoalToGoalFlow');
        modal.remove();
        return <></>;
    }

    if (!sourceGoalAccount) {
        // This should never happen
        console.warn(
            'Failed to find source goal account in OnetimeGoalToGoalFlow'
        );
        modal.remove();
        return <></>;
    }

    const fullWithdrawalDescription = getFullWithdrawalDescription({
        amount,
        sourceBalance,
        sourceGoalAccountInvestmentType: sourceGoalAccount.info.investmentType,
        pendingWithdrawalAmount,
    });

    const onClose = () => {
        modal.remove();
    };

    const destinationGoalFrame: FrameRenderer = {
        id: 'DestinationGoal',
        render: ({ goForward }) => {
            return (
                <TransactionDestinationGoalCard
                    title="Transfer money between goals"
                    subtitle="When we transfer money between goals, we’ll first sell your investment then move money into the new goal. There may be tax consequences as part of this."
                    sourceGoal={sourceGoal}
                    destinationGoal={destinationGoal}
                    onChangeDestinationGoal={setDestinationGoal}
                    onNext={goForward}
                    onClose={onClose}
                />
            );
        },
    };

    const amountFrame: FrameRenderer = {
        id: 'Amount',
        render: ({ goBack, goForward }) => {
            if (!sourceId) return <></>;

            return (
                <TransactionAmountCard
                    title="How much do you want to move?"
                    subtitle="Moving money to another goal usually takes 1-2 business days."
                    sourceId={sourceId}
                    amount={amount}
                    onChangeAmount={setAmount}
                    onBack={goBack}
                    onNext={goForward}
                    onClose={onClose}
                    pendingWithdrawalAmount={pendingWithdrawalAmount}
                    isCappedAtAvailableBalance={isCappedAtAvailableBalance}
                    fullWithdrawalDescription={fullWithdrawalDescription}
                />
            );
        },
    };

    const confirmationFrame: FrameRenderer = {
        id: 'Confirmation',
        render: ({ goBack, goForward }) => {
            const destinationId = destinationGoal
                ? getMyGoalAccount(destinationGoal)?.id
                : undefined;
            if (!destinationId) return <></>;

            return (
                <TransactionConfirmationCard
                    title="Please review your transfer details"
                    nextButtonText="Transfer funds"
                    sourceId={sourceId}
                    destinationId={destinationId}
                    amount={amount}
                    fullWithdrawalDescription={fullWithdrawalDescription}
                    goalDirection={goalDirection}
                    goalId={goalId}
                    pendingWithdrawalAmount={pendingWithdrawalAmount}
                    date={dt().toDateStr()}
                    onBack={goBack}
                    onCompleted={goForward}
                    onClose={onClose}
                />
            );
        },
    };

    const completeFrame: FrameRenderer = {
        id: 'Complete',
        render: () => {
            return (
                <TransactionCompleteCard
                    title="Transfer inititiated!"
                    onClose={onClose}
                />
            );
        },
    };

    const frames: FrameRenderer[] = [
        destinationGoalFrame,
        amountFrame,
        confirmationFrame,
        completeFrame,
    ];

    return (
        <Modal size="md" isOpen={modal.visible} onClose={onClose} unstyled>
            <TransactionSlideAnimator frames={frames} />
        </Modal>
    );
};

const OnetimeGoalToGoalFlowModal =
    ModalManager.create<OnetimeGoalToGoalFlowProps>(OnetimeGoalToGoalFlow);

export const showOnetimeGoalToGoalFlowModal = (
    props: OnetimeGoalToGoalFlowProps
) => ModalManager.show(OnetimeGoalToGoalFlowModal, props);
